import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useIncidents } from '../../context/IncidentContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

const { FiSave, FiX, FiZap, FiUpload, FiImage, FiTrash2, FiAlertCircle } = FiIcons;

const CreateIncident = () => {
    const navigate = useNavigate();
    const { createIncident, fetchIncidents } = useIncidents();
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        service: '',
        category: '',
        subcategory: '',
        impact: 'Medium',
        urgency: 'Medium',
        priority: 'Medium',
        assignedTo: '',
        assignee: '',
        tags: '',
    });

    const [customFields, setCustomFields] = useState([]);
    const [customFieldValues, setCustomFieldValues] = useState({});
    const [loadingFields, setLoadingFields] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fieldLoadError, setFieldLoadError] = useState(null);

    const [aiSuggestions, setAiSuggestions] = useState(null);
    const [showAiSuggestions, setShowAiSuggestions] = useState(false);
    const [attachedImages, setAttachedImages] = useState([]);

    const services = [
        'Email Service',
        'Network Infrastructure',
        'Database Server',
        'Web Application',
        'File Server',
        'VPN Service',
        'Other'
    ];

    const categories = [
        'Hardware',
        'Software',
        'Network',
        'Security',
        'Database',
        'Email',
        'Access'
    ];

    const subcategories = {
        'Hardware': ['Server', 'Workstation', 'Printer', 'Mobile Device'],
        'Software': ['Application', 'Operating System', 'Driver', 'Browser'],
        'Network': ['Connectivity', 'Performance', 'VPN', 'WiFi'],
        'Security': ['Access Control', 'Malware', 'Phishing', 'Policy Violation'],
        'Database': ['Performance', 'Connectivity', 'Data Loss', 'Backup'],
        'Email': ['Delivery', 'Authentication', 'Storage', 'Sync'],
        'Access': ['Password Reset', 'Account Locked', 'Permissions', 'New Access']
    };

    const impactLevels = ['High', 'Medium', 'Low'];
    const urgencyLevels = ['High', 'Medium', 'Low'];

    // Load custom fields on component mount
    useEffect(() => {
        loadCustomFields();
    }, []);

    const loadCustomFields = async () => {
        console.log('🔄 Loading custom fields for incidents...');
        setLoadingFields(true);
        setFieldLoadError(null);

        try {
            // Fetch custom fields for incidents from the API
            const response = await api.get('/custom-fields?entityType=incident');

            if (response.success && response.data) {
                console.log(`✅ Loaded ${response.data.length} custom fields:`, response.data);
                setCustomFields(response.data);

                // Initialize custom field values with default values
                const initialValues = {};
                response.data.forEach(field => {
                    console.log(`  - Field: ${field.name} (${field.type}) ${field.isRequired ? '[REQUIRED]' : ''}`);

                    if (field.defaultValue) {
                        initialValues[field.fieldKey] = field.defaultValue;
                    } else {
                        // Set appropriate default based on type
                        switch (field.type) {
                            case 'boolean':
                                initialValues[field.fieldKey] = false;
                                break;
                            case 'number':
                                initialValues[field.fieldKey] = '';
                                break;
                            default:
                                initialValues[field.fieldKey] = '';
                        }
                    }
                });

                console.log('📝 Initial custom field values:', initialValues);
                setCustomFieldValues(initialValues);

                if (response.data.length === 0) {
                    console.warn('⚠️ No custom fields returned from API');
                }
            } else {
                console.error('❌ API response missing success or data:', response);
                setFieldLoadError('Invalid API response');
            }
        } catch (error) {
            console.error('❌ Error loading custom fields:', error);
            setFieldLoadError(error.message || 'Failed to load custom fields');
            toast.error('Failed to load custom fields. Using default form.');
        } finally {
            setLoadingFields(false);
            console.log('✅ Custom fields loading complete');
        }
    };

    // Calculate priority based on impact and urgency
    const calculatePriority = (impact, urgency) => {
        if (impact === 'High' && urgency === 'High') return 'Critical';
        if ((impact === 'High' && urgency === 'Medium') || (impact === 'Medium' && urgency === 'High')) return 'High';
        if ((impact === 'High' && urgency === 'Low') || (impact === 'Medium' && urgency === 'Medium') || (impact === 'Low' && urgency === 'High')) return 'Medium';
        return 'Low';
    };

    const handleInputChange = (field, value) => {
        const newFormData = { ...formData, [field]: value };

        // Auto-calculate priority when impact or urgency changes
        if (field === 'impact' || field === 'urgency') {
            newFormData.priority = calculatePriority(
                field === 'impact' ? value : formData.impact,
                field === 'urgency' ? value : formData.urgency
            );
        }

        setFormData(newFormData);

        // Trigger AI suggestions when title or description changes
        if ((field === 'title' || field === 'description') && value.length > 10) {
            triggerAiSuggestions(newFormData);
        }
    };

    const handleCustomFieldChange = (fieldKey, value) => {
        console.log(`📝 Custom field changed: ${fieldKey} = ${value}`);
        setCustomFieldValues(prev => ({
            ...prev,
            [fieldKey]: value
        }));
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
            id: Date.now() + Math.random(),
            name: file.name,
            size: file.size,
            type: file.type,
            url: URL.createObjectURL(file),
            file: file
        }));

        setAttachedImages(prev => [...prev, ...newImages]);
    };

    const removeImage = (imageId) => {
        setAttachedImages(prev => {
            const image = prev.find(img => img.id === imageId);
            if (image && image.url.startsWith('blob:')) {
                URL.revokeObjectURL(image.url);
            }
            return prev.filter(img => img.id !== imageId);
        });
    };

    const triggerAiSuggestions = (data) => {
        // Simulate AI analysis
        setTimeout(() => {
            const suggestions = {
                category: data.title.toLowerCase().includes('network') ? 'Network' :
                    data.title.toLowerCase().includes('email') ? 'Email' :
                        data.title.toLowerCase().includes('database') ? 'Database' : 'Software',
                service: data.title.toLowerCase().includes('email') ? 'Email Service' : 'Network Infrastructure',
                tags: ['auto-suggested', data.title.toLowerCase().includes('urgent') ? 'urgent' : 'standard'],
                knowledgeArticles: [
                    { id: 'KB-001', title: 'Network Troubleshooting Guide', relevance: 95 },
                    { id: 'KB-002', title: 'Common Email Issues', relevance: 87 }
                ]
            };

            setAiSuggestions(suggestions);
            setShowAiSuggestions(true);
        }, 1000);
    };

    const applyAiSuggestions = () => {
        if (aiSuggestions) {
            setFormData(prev => ({
                ...prev,
                category: aiSuggestions.category,
                service: aiSuggestions.service,
                tags: aiSuggestions.tags.join(', ')
            }));
            setShowAiSuggestions(false);
        }
    };

    const validateForm = () => {
        console.log('🔍 Validating form...');

        // Validate required core fields
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return false;
        }
        if (!formData.description.trim()) {
            toast.error('Description is required');
            return false;
        }
        if (!formData.service) {
            toast.error('Affected Service is required');
            return false;
        }
        if (!formData.category) {
            toast.error('Category is required');
            return false;
        }

        // Validate required custom fields
        console.log(`📋 Validating ${customFields.length} custom fields...`);
        for (const field of customFields) {
            if (field.isRequired) {
                const value = customFieldValues[field.fieldKey];
                console.log(`  - Checking required field: ${field.name} (${field.fieldKey}) = ${value}`);

                // Check if value is empty
                if (value === undefined || value === null || value === '') {
                    console.error(`❌ Required field missing: ${field.name}`);
                    toast.error(`${field.name} is required`);
                    return false;
                }

                // Type-specific validation
                if (field.type === 'select' && !field.options.includes(value)) {
                    console.error(`❌ Invalid select value: ${field.name} = ${value}`);
                    toast.error(`Invalid value for ${field.name}`);
                    return false;
                }

                if (field.type === 'number' && isNaN(value)) {
                    console.error(`❌ Invalid number value: ${field.name} = ${value}`);
                    toast.error(`${field.name} must be a number`);
                    return false;
                }
            }

            // Validate select field values are in options
            if (field.type === 'select' && customFieldValues[field.fieldKey]) {
                if (!field.options.includes(customFieldValues[field.fieldKey])) {
                    console.error(`❌ Invalid select option: ${field.name}`);
                    toast.error(`Invalid value for ${field.name}`);
                    return false;
                }
            }
        }

        console.log('✅ Form validation passed');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            // Step 1: Create the incident
            const incidentPayload = {
                title: formData.title,
                description: formData.description,
                priority: formData.priority,
                service: formData.service,
                category: formData.category,
                subcategory: formData.subcategory,
                impact: formData.impact,
                urgency: formData.urgency,
                assignedTo: formData.assignedTo,
                assignee: formData.assignee,
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
                attachments: attachedImages.map(img => ({
                    name: img.name,
                    size: img.size,
                    type: img.type
                })),
            };

            // For now, using mock incident creation from context
            // In production, this would be: const incidentResponse = await api.post('/incidents', incidentPayload);
            const newIncident = await createIncident(incidentPayload);
            const incidentId = newIncident.id;

            // Step 2: Save custom field values if any exist
            if (customFields.length > 0) {
                const customFieldPayload = {
                    entityType: 'Incident',
                    entityId: incidentId,
                    fields: Object.entries(customFieldValues)
                        .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
                        .map(([key, value]) => {
                            // Find the corresponding custom field to get its id
                            const field = customFields.find(f => f.fieldKey === key);
                            return {
                                key,
                                custom_field_id: field ? field.id : null, // Add custom_field_id
                                value: typeof value === 'boolean' ? value.toString() : value
                            };
                        })
                        .filter(f => f.custom_field_id !== null) // Optional: remove entries without id
                };

                // Only send if there are fields to save
                if (customFieldPayload.fields.length > 0) {
                    try {
                        const customFieldResponse = await api.post('/custom-fields/values', customFieldPayload);
                        fetchIncidents(); // Refresh incidents to reflect new custom field values
                    } catch (error) {
                        console.error('❌ Error saving custom field values:', error);
                        // Don't fail the whole operation if custom fields fail
                        toast.error('Incident created but custom fields failed to save');
                    }
                } else {
                    console.log('ℹ️ No custom field values to save');
                }
            } else {
                console.log('ℹ️ No custom fields configured');
            }

            toast.success('Incident created successfully');
            navigate(`/incidents/${incidentId}`);

        } catch (error) {
            console.error('❌ Error creating incident:', error);
            toast.error(error.message || 'Failed to create incident');
        } finally {
            setSubmitting(false);
        }
    };

    const renderCustomField = (field) => {
        const value = customFieldValues[field.fieldKey] || '';

        switch (field.type) {
            case 'text':
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                        placeholder={`Enter ${field.name.toLowerCase()}`}
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        required={field.isRequired}
                    />
                );

            case 'number':
                return (
                    <input
                        type="number"
                        value={value}
                        onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                        placeholder={`Enter ${field.name.toLowerCase()}`}
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        required={field.isRequired}
                    />
                );

            case 'date':
                return (
                    <input
                        type="date"
                        value={value}
                        onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        required={field.isRequired}
                    />
                );

            case 'boolean':
                return (
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={value === true || value === 'true'}
                            onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.checked)}
                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Yes</span>
                    </label>
                );

            case 'select':
                return (
                    <select
                        value={value}
                        onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        required={field.isRequired}
                    >
                        <option value="">Select an option</option>
                        {field.options && field.options.map((option, index) => (
                            <option key={index} value={option}>{option}</option>
                        ))}
                    </select>
                );

            default:
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                        placeholder={`Enter ${field.name.toLowerCase()}`}
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        required={field.isRequired}
                    />
                );
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-6 h-full overflow-y-auto"
        >
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Incident</h1>
                    <p className="text-gray-600 dark:text-gray-400">Report a new service incident</p>
                </div>
                <button
                    onClick={() => navigate('/incidents')}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Main Form */}
                <div className="lg:col-span-3">
                    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="space-y-6">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    placeholder="Brief description of the incident"
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder="Detailed description of the incident including steps to reproduce, error messages, etc."
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Screenshot Attachment */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Attach Screenshots
                                </label>
                                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        id="image-upload"
                                    />
                                    <label
                                        htmlFor="image-upload"
                                        className="cursor-pointer inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                    >
                                        <SafeIcon icon={FiUpload} className="w-4 h-4" />
                                        <span>Choose Images</span>
                                    </label>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                        or drag and drop screenshots here
                                    </p>
                                </div>

                                {/* Attached Images */}
                                {attachedImages.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Attached Images:</p>
                                        {attachedImages.map(image => (
                                            <div key={image.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="flex items-center space-x-3">
                                                    <SafeIcon icon={FiImage} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{image.name}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {(image.size / 1024).toFixed(1)} KB
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(image.id)}
                                                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                >
                                                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Service and Category */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Affected Service <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        value={formData.service}
                                        onChange={(e) => handleInputChange('service', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="">Select a service</option>
                                        {services.map(service => (
                                            <option key={service} value={service}>{service}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        value={formData.category}
                                        onChange={(e) => handleInputChange('category', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="">Select a category</option>
                                        {categories.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Subcategory */}
                            {formData.category && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Subcategory
                                    </label>
                                    <select
                                        value={formData.subcategory}
                                        onChange={(e) => handleInputChange('subcategory', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="">Select a subcategory</option>
                                        {subcategories[formData.category]?.map(sub => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Impact, Urgency, Priority */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Impact <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        value={formData.impact}
                                        onChange={(e) => handleInputChange('impact', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        {impactLevels.map(level => (
                                            <option key={level} value={level}>{level}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Urgency <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        value={formData.urgency}
                                        onChange={(e) => handleInputChange('urgency', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        {urgencyLevels.map(level => (
                                            <option key={level} value={level}>{level}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Priority (Auto-calculated)
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.priority}
                                        disabled
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                                    />
                                </div>
                            </div>

                            {/* Assignment */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assignment Group
                                    </label>
                                    <select
                                        value={formData.assignedTo}
                                        onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="">Select assignment group</option>
                                        <option value="Network Team">Network Team</option>
                                        <option value="Application Team">Application Team</option>
                                        <option value="Database Team">Database Team</option>
                                        <option value="Security Team">Security Team</option>
                                        <option value="Hardware Team">Hardware Team</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assignee
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.assignee}
                                        onChange={(e) => handleInputChange('assignee', e.target.value)}
                                        placeholder="Specific person (optional)"
                                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>

                            {/* Custom Fields Section */}
                            {loadingFields ? (
                                <div className="py-8 text-center border-t border-gray-200 dark:border-gray-700">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">Loading custom fields...</p>
                                </div>
                            ) : fieldLoadError ? (
                                <div className="py-6 text-center border-t border-gray-200 dark:border-gray-700">
                                    <div className="inline-flex items-center space-x-2 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg">
                                        <SafeIcon icon={FiAlertCircle} className="w-5 h-5" />
                                        <span className="text-sm font-medium">{fieldLoadError}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={loadCustomFields}
                                        className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                        Retry loading custom fields
                                    </button>
                                </div>
                            ) : customFields.length > 0 ? (
                                <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Additional Information</h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                Fill in the custom fields defined by your organization ({customFields.length} fields)
                                            </p>
                                        </div>
                                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs font-medium rounded-full">
                                            {customFields.filter(f => f.isRequired).length} required
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {customFields.map(field => (
                                            <div key={field.id}>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    {field.name} {field.isRequired && <span className="text-red-500">*</span>}
                                                </label>
                                                {renderCustomField(field)}
                                                {field.defaultValue && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        Default: {field.defaultValue}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="py-4 text-center border-t border-gray-200 dark:border-gray-700">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        No custom fields configured for incidents
                                    </p>
                                </div>
                            )}

                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Tags
                                </label>
                                <input
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) => handleInputChange('tags', e.target.value)}
                                    placeholder="Comma-separated tags (e.g., urgent, datacenter, email)"
                                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Submit Buttons */}
                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                                    <span>{submitting ? 'Creating...' : 'Create Incident'}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate('/incidents')}
                                    disabled={submitting}
                                    className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {/* AI Suggestions Sidebar */}
                <div className="space-y-6">
                    {/* AI Suggestions */}
                    {showAiSuggestions && aiSuggestions && (
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700"
                        >
                            <div className="flex items-center space-x-2 mb-3">
                                <SafeIcon icon={FiZap} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                <h3 className="font-medium text-blue-900 dark:text-blue-100">AI Suggestions</h3>
                            </div>

                            <div className="space-y-3 text-sm">
                                <div>
                                    <p className="text-gray-600 dark:text-gray-400">Suggested Category:</p>
                                    <p className="font-medium text-gray-900 dark:text-white">{aiSuggestions.category}</p>
                                </div>

                                <div>
                                    <p className="text-gray-600 dark:text-gray-400">Suggested Service:</p>
                                    <p className="font-medium text-gray-900 dark:text-white">{aiSuggestions.service}</p>
                                </div>

                                <div>
                                    <p className="text-gray-600 dark:text-gray-400">Suggested Tags:</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {aiSuggestions.tags.map(tag => (
                                            <span key={tag} className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded text-xs">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={applyAiSuggestions}
                                className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                            >
                                Apply Suggestions
                            </button>
                        </motion.div>
                    )}

                    {/* Knowledge Articles */}
                    {aiSuggestions?.knowledgeArticles && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Related Knowledge</h3>
                            <div className="space-y-2">
                                {aiSuggestions.knowledgeArticles.map(article => (
                                    <div key={article.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <p className="font-medium text-sm text-blue-600 dark:text-blue-400">{article.id}</p>
                                        <p className="text-sm text-gray-900 dark:text-white">{article.title}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{article.relevance}% relevant</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Help */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                        <h3 className="font-medium text-gray-900 dark:text-white mb-3">Need Help?</h3>
                        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                            <p>• Be specific in your title</p>
                            <p>• Include error messages in description</p>
                            <p>• Select the correct affected service</p>
                            <p>• Use tags for better categorization</p>
                            <p>• Attach screenshots for visual context</p>
                            <p>• Fill in all required custom fields</p>
                        </div>
                    </div>

                    {/* Debug Info (Development Only) */}
                    {process.env.NODE_ENV === 'development' && (
                        <div className="bg-gray-100 dark:bg-gray-900 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
                            <h3 className="font-mono text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">🐛 Debug Info</h3>
                            <div className="space-y-1 text-xs font-mono text-gray-600 dark:text-gray-400">
                                <p>Custom Fields: {customFields.length}</p>
                                <p>Loading: {loadingFields ? 'Yes' : 'No'}</p>
                                <p>Error: {fieldLoadError || 'None'}</p>
                                <p>Values: {Object.keys(customFieldValues).length}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default CreateIncident;